import React from 'react'
import { ButtonGroup } from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { J2Add, J2Subtract } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'
import QuantityInput from './QuantityInput'

interface ButtonQuantityGroupProps {
  quantity: number
  setQuantity: (value: number | ((prev: number) => number)) => void
  minQuantity?: number
  maxQuantity?: number
  isLoading?: boolean
}

const useStyles = makeStyles()(theme => ({
  buttonGroup: {
    border: `1px solid ${theme.palette.shades.shade02}`,
    backgroundColor: theme.palette.shades.shade04,
    boxShadow: 'none',
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      bottom: theme.spacing(1),
      left: '50%',
      transform: 'translateX(-50%)',
      marginBottom: theme.spacing(1),
    },
  },
  button: {
    backgroundColor: theme.palette.shades.shade04,
    borderRight: 'none !important',
    fontSize: '24px',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  customInput: {
    borderRadius: 0,
    border: 'none',
    background: 'transparent',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& input': {
      padding: 0,
    },
  },
}))

/*
 * This component shows button quantity for products in the checkout
 */
const ButtonQuantityGroup: React.FC<ButtonQuantityGroupProps> = ({ quantity, setQuantity, minQuantity = 0, maxQuantity, isLoading = false }) => {
  const { classes } = useStyles()

  const handleIncrease = (): void => setQuantity(prev => prev + 1)
  const handleDecrease = (): void => setQuantity(prev => prev - 1)
  const handleChange = (quantity: number): void => setQuantity(quantity)

  return (
    <ButtonGroup className={classes.buttonGroup} color="inherit">
      <LoadingButton className={classes.button} onClick={handleDecrease} disabled={quantity <= (minQuantity ?? 0) || isLoading}>
        <J2Subtract />
      </LoadingButton>
      <QuantityInput
        quantity={quantity}
        minQuantity={minQuantity}
        maxQuantity={maxQuantity}
        onQuantityChange={handleChange}
        disabled={isLoading}
        inputClassName={classes.customInput}
      />
      <LoadingButton
        className={classes.button}
        onClick={handleIncrease}
        disabled={(maxQuantity !== undefined && quantity >= maxQuantity) || isLoading}
      >
        <J2Add />
      </LoadingButton>
    </ButtonGroup>
  )
}

export default ButtonQuantityGroup
